import type { BrandSpecial } from '@iheartjane/data-access';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import type { AppMode, Breadcrumb, PriceId } from '@iheartjane/types';
import type { ReactNode } from 'react';
import { createContext, useContext, useMemo, useState } from 'react';
import type { DeepReadonly } from 'ts-essentials';

import type { AlgoliaProduct, JaneSearchState } from '@jane/search/types';
import { isSortedByPrice } from '@jane/search/util';
// eslint-disable-next-line no-restricted-imports
import type {
  Brand,
  MenuProduct,
  Product,
  ReservationCartProduct,
  Store,
  StoreSpecial,
} from '@jane/shared/models';
import type {
  AlmostGone,
  EcommPaths,
  UserLocation,
  MenuProduct as ZMenuProduct,
} from '@jane/shared/types';
import type { MenuRowRules } from '@jane/shared/util';
import {
  defaultWeightIdForMenuProduct,
  defaultWeightIdForMenuProductFromFilters,
  findDefaultWeightForKind,
  isSoldByWeight,
} from '@jane/shared/util';

import { firstAvailablePriceIdFromFilters } from './legacyMenuProductCard/utils/firstAvailablePriceIdFromFilters';

export enum DisplayMode {
  Confirmation = 'confirmation',
  Edit = 'edit',
  Product = 'product',
}

export type AddData = {
  almostGone?: AlmostGone;
  count: number;
  location: 'menu' | 'productDetailPage' | 'pendingCart' | 'marketplaceProduct';
  menuProduct: MenuProduct | ZMenuProduct;
  price_id: PriceId;
  special?: StoreSpecial;
  store: DeepReadonly<Store>;
};
export type DeleteData = {
  itemId: number;
  selectedWeight: PriceId | null;
};

export interface ProductCardContextProps {
  appliedWeightFilter: PriceId | '';
  appMode: AppMode;
  availableWeights?: PriceId[] | null;
  brand?: Brand;
  brandSpecials?: BrandSpecial[];
  breadcrumbs?: Breadcrumb[];
  carouselView?: boolean;
  cartIsOpen: boolean;
  cartProduct?: ReservationCartProduct[];
  compareOverlay?: ReactNode;
  currentSpecial?: StoreSpecial;
  customerId: number | null;
  defaultWeight: PriceId;
  disableInteraction?: boolean;
  disableWeightFilter?: boolean;
  displayMode: DisplayMode;
  fromAllSpecials?: boolean | null;
  fromSpecialId?: string | number | null;
  hasNonStandardDefaultWeight: boolean;
  hideActions?: boolean;
  janeGoldLabel?: string | null;
  listView?: boolean;
  maxWeightOption?: number | null;
  menuProduct?: MenuProduct;
  menuRowRules?: MenuRowRules;
  onAddToCart?: (addData: AddData) => void;
  onCloseCart?: () => void;
  onDeleteFromCart?: (deleteData: DeleteData) => void;
  pdpPath?: EcommPaths['menuProduct'];
  product?: Product;
  searchState: JaneSearchState<AlgoliaProduct>;
  selectedWeight: PriceId;
  setDisplayMode: (arg: DisplayMode) => void;
  setSelectedWeight: (arg: PriceId) => void;
  showOnlyWeights?: string[] | null;
  store?: DeepReadonly<Store>;
  trackListViewClick?: () => void;
  userLocation: UserLocation | null;
  virtualBudtenderDescription?: string;
  weightFilter?: (
    weight: string,
    product: ZMenuProduct | AlgoliaProduct
  ) => boolean;
}

const defaultContext: ProductCardContextProps = {
  appMode: 'default',
  appliedWeightFilter: '',
  carouselView: false,
  disableWeightFilter: false,
  cartIsOpen: false,
  cartProduct: [],
  customerId: null,
  defaultWeight: 'gram',
  displayMode: DisplayMode.Product,
  hasNonStandardDefaultWeight: false,
  listView: false,
  searchState: {},
  selectedWeight: 'gram',
  setDisplayMode: () => {},
  setSelectedWeight: () => {},
  userLocation: null,
};

const ProductCardContext =
  createContext<ProductCardContextProps>(defaultContext);

export const ProductCardProvider = ({
  children,
  value,
}: {
  children: ReactNode;
  value: Partial<ProductCardContextProps>;
}) => {
  const [displayMode, setDisplayMode] = useState(DisplayMode.Product);
  const handleNSW = useFlag(FLAGS.cnSpecialDetailPageNsw);

  // When weight filtering is disabled (e.g. mid-grid ad row),
  // neutralize weight filter inputs so existing logic selects defaults naturally.
  const weightFilter = value.disableWeightFilter
    ? undefined
    : value.weightFilter;
  const appliedWeightFilter: PriceId | '' = value.disableWeightFilter
    ? ''
    : (value.appliedWeightFilter ?? '');
  const searchState = useMemo(() => {
    if (!value.disableWeightFilter) return value.searchState;
    return {
      ...value.searchState,
      filters: {
        ...value.searchState?.filters,
        available_weights: undefined,
      },
    };
  }, [value.disableWeightFilter, value.searchState]);

  // If using the new specialWeights functions, we take all variables into account
  // and determine the available_weights here to be consumed by other components.
  const availableWeights = useMemo(() => {
    if (value.menuProduct && weightFilter) {
      if (!isSoldByWeight(value.menuProduct.kind)) {
        return [];
      }

      const weights =
        value.menuProduct.available_weights?.map((weight) =>
          weight.replace(' ', '_')
        ) ?? [];

      const filteredWeights = weights?.filter(
        (weight) =>
          !weightFilter ||
          weightFilter(weight, value.menuProduct as ZMenuProduct)
      );

      if (filteredWeights.length === 0 && weights.length > 0) {
        return weights as PriceId[];
      }

      return filteredWeights as PriceId[];
    }
    return null;
  }, [value.menuProduct, weightFilter, value.showOnlyWeights]);

  const standardDefaultWeight = useMemo(() => {
    return defaultWeightIdForMenuProduct({
      menuProduct: value.menuProduct as MenuProduct,
    });
  }, [value.menuProduct]);

  // This value is consumed by the listViewContainer to determine which weight is autoselected.
  // If the above availableWeights returns a value, we use that array as the starting source of truth.
  const defaultWeight: PriceId | null = useMemo(() => {
    if (availableWeights) {
      if (!availableWeights?.length) return 'each';
      if (value?.cartProduct?.length) return value.cartProduct[0].price_id;

      const defaultWeightByKind =
        isSortedByPrice(searchState) ||
        searchState?.filters?.available_weights?.length
          ? (availableWeights[0] ??
            findDefaultWeightForKind((value.menuProduct as MenuProduct).kind))
          : (findDefaultWeightForKind(
              (value.menuProduct as MenuProduct).kind
            ) ?? availableWeights[0]);

      const defaultWeight = availableWeights.includes(defaultWeightByKind)
        ? defaultWeightByKind
        : availableWeights[0];

      if (searchState?.filters?.available_weights) {
        return firstAvailablePriceIdFromFilters({
          menuProduct: value.menuProduct as MenuProduct,
          appliedFilters: searchState.filters.available_weights as string[],
          defaultValue: defaultWeight,
        });
      }

      return defaultWeight;
    } else {
      const weightFilters = searchState?.filters?.available_weights;
      return weightFilters && weightFilters.length > 1
        ? defaultWeightIdForMenuProductFromFilters({
            appliedWeightFilters: weightFilters as string[],
            menuProduct: value.menuProduct as MenuProduct,
          })
        : defaultWeightIdForMenuProduct({
            menuProduct: value.menuProduct as MenuProduct,
            menuRowRules: value.menuRowRules,
            special: value.currentSpecial,
            appliedWeightFilter: appliedWeightFilter,
            sortedByPrice: isSortedByPrice(searchState),
            showOnlyWeights: value.showOnlyWeights,
            applyNswFix: handleNSW,
          });
    }
  }, [
    availableWeights,
    appliedWeightFilter,
    value.cartProduct,
    value.currentSpecial,
    value.menuRowRules,
    searchState,
  ]);

  // If we've changed the default weight (due to a special not applying to that weight or sorting by price),
  // make note of this for use later. But disregard if the product is in the cart.
  const hasNonStandardDefaultWeight =
    !value?.cartProduct?.length && standardDefaultWeight !== defaultWeight;

  const providerValue = useMemo(
    () => ({
      ...defaultContext,
      ...value,
      appliedWeightFilter,
      availableWeights,
      defaultWeight,
      displayMode: value.displayMode || displayMode,
      hasNonStandardDefaultWeight,
      setDisplayMode,
    }),
    [
      availableWeights,
      displayMode,
      defaultWeight,
      hasNonStandardDefaultWeight,
      value,
      setDisplayMode,
    ]
  );

  return (
    <ProductCardContext.Provider value={providerValue}>
      {children}
    </ProductCardContext.Provider>
  );
};

export const useProductCardContext = () => {
  const context = useContext(ProductCardContext);

  if (context === undefined) {
    throw new Error(
      'useProductCardContext must be used within a ProductCardProvider'
    );
  }

  return context;
};
